import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { loadMock, recordStation } from "@/lib/mock";
import { ArrowRight, Lightbulb, Flag, Loader2, CheckCircle2, GraduationCap } from "lucide-react";
import { toast } from "sonner";
import { http, runJob, errMsg, DOMAIN_SHORT } from "@/lib/api";
import { useRecorder } from "@/hooks/useRecorder";
import { VivaTimer } from "@/components/VivaTimer";
import { HintBox, AnswerPanel } from "@/components/VivaParts";
import { RichText } from "@/components/RichText";
import { ReadingNotes, FloatingNotes, useNotes } from "@/components/NotesBox";

const TOTAL = 15 * 60;
const READING = 3 * 60;

const Stem = ({ c }) => (
  <section data-testid="viva-stem-container" className="card p-5 sm:p-7 fade-up">
    <div className="flex flex-wrap items-center gap-2 text-xs font-mono">
      <span className="px-2 py-1 rounded-md" style={{ background: "var(--muted-bg)", color: "var(--accent)" }}>D{c.domain} · {DOMAIN_SHORT[c.domain - 1]}</span>
      <span data-testid="viva-case-source" className="px-2 py-1 rounded-md" style={{ background: "var(--muted-bg)", color: "var(--ink-3)" }}>
        {c.source === "ai" ? "AI-written case" : c.source === "college" ? `ANZCA sample viva ${c.number}` : `Bank station ${c.number}${c.collection && c.collection !== "Station bank" ? ` · ${c.collection}` : ""}`}
      </span>
    </div>
    <h1 data-testid="viva-case-title" className="mt-4 font-display text-2xl sm:text-3xl font-bold tracking-tight pr-36 sm:pr-0">{c.title}</h1>
    {c.source === "college" && (
      <p data-testid="viva-college-note" className="mt-2 text-xs flex items-center gap-1.5" style={{ color: "var(--ink-3)" }}>
        <GraduationCap size={13} /> From the ANZCA RGA-SSSA Sample Exam Questions document (September 2024), reproduced verbatim.
      </p>
    )}
    <div className="mt-5 space-y-2.5 text-[15px] leading-relaxed" style={{ color: "var(--ink-2)" }}>
      {c.stem.split("\n").map((l, i) => <p key={i}><RichText text={l} /></p>)}
    </div>
  </section>
);

const splitParts = (prompt) =>
  prompt.split("\n").reduce((parts, line) => {
    if (!parts.length || /^Question \d+[a-z]?:/.test(line.trim())) parts.push(line);
    else parts[parts.length - 1] += `\n${line}`;
    return parts;
  }, []);

const QuestionPrompt = ({ prompt }) => {
  const parts = splitParts(prompt);
  const [shown, setShown] = useState(1);
  if (parts.length === 1) {
    return <h2 data-testid="viva-question-prompt" className="mt-3 font-display text-lg font-semibold leading-snug whitespace-pre-line">{prompt.includes("\n") ? prompt : `“${prompt}”`}</h2>;
  }
  return (
    <div data-testid="viva-question-prompt" className="mt-3 space-y-3">
      {parts.slice(0, shown).map((p, k) => (
        <h2 key={k} data-testid={`viva-question-part-${k}`} className={`font-display leading-snug whitespace-pre-line fade-up ${k === shown - 1 ? "text-lg font-semibold" : "text-sm opacity-70"}`}>{p}</h2>
      ))}
      {shown < parts.length && (
        <button data-testid="viva-next-part-button" onClick={() => setShown((s) => s + 1)} className="btn-ghost h-9 px-4 rounded-full text-sm font-medium">
          Examiner continues: next part ({shown + 1} of {parts.length})
        </button>
      )}
    </div>
  );
};

const ExaminerPrompts = ({ items: raw }) => {
  const [open, setOpen] = useState(false);
  const items = (raw || []).filter((t) => !/^\(Ask if the candidate does not volunteer/i.test(t.trim()));
  if (!items.length) return null;
  return (
    <div className="mt-3">
      <button data-testid="viva-examiner-prompts-toggle" onClick={() => setOpen((o) => !o)} className="text-xs font-medium underline" style={{ color: "var(--accent)" }}>
        {open ? "Hide" : "Stuck? Show"} examiner prompts ({items.length})
      </button>
      {open && (
        <ul data-testid="viva-examiner-prompts" className="mt-2 space-y-1 text-sm rounded-lg p-3" style={{ background: "var(--muted-bg)", color: "var(--ink-2)" }}>
          {items.map((t, i) => <li key={i}>{t}</li>)}
        </ul>
      )}
    </div>
  );
};

const PastQuestion = ({ q, i, transcript, pending }) => (
  <div data-testid={`viva-past-question-${i}`} className="card px-5 py-3 flex items-start gap-3 opacity-80">
    <CheckCircle2 size={16} className="mt-0.5 shrink-0" style={{ color: "var(--ok)" }} />
    <div className="text-sm">
      <span className="font-semibold">Q{i + 1}. {q.prompt}</span>
      <p className="mt-1 line-clamp-2" style={{ color: "var(--ink-3)" }}>
        {transcript && pending && <span data-testid={`viva-past-still-transcribing-${i}`} className="inline-flex items-center gap-1 mr-1.5 text-xs" style={{ color: "var(--accent)" }}><Loader2 size={11} className="animate-spin" /> still transcribing…</span>}
        {transcript || (pending ? <span data-testid={`viva-past-transcribing-${i}`} className="inline-flex items-center gap-1.5"><Loader2 size={12} className="animate-spin" /> Transcribing your answer…</span> : "No answer recorded")}
      </p>
    </div>
  </div>
);

export default function Viva() {
  const { caseId } = useParams();
  const nav = useNavigate();
  const [c, setC] = useState(null);
  const [phase, setPhase] = useState(-1);
  const [elapsed, setElapsed] = useState(0);
  const [phaseStart, setPhaseStart] = useState(0);
  const [answers, setAnswers] = useState({});
  const [hints, setHints] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [notes, setNotes] = useNotes(caseId);
  const [params] = useSearchParams();
  const isMock = params.get("mock") === "1" && !!loadMock();
  const answersRef = useRef({});
  answersRef.current = answers;

  const onTranscript = useCallback((i, text) => setAnswers((a) => ({ ...a, [i]: a[i] ? `${a[i]} ${text}` : text })), []);
  const onError = useCallback((m) => toast.error(m), []);
  const rec = useRecorder(onTranscript, onError);

  useEffect(() => {
    http.get(`/cases/${caseId}`).then((r) => setC(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [caseId]);

  useEffect(() => {
    if (!c || submitting) return;
    const t = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => clearInterval(t);
  }, [c, submitting]);

  useEffect(() => () => rec.stop(), [rec.stop]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!c) return <div className="p-10 flex justify-center"><Loader2 className="animate-spin" /></div>;

  const q = phase >= 0 ? c.questions[phase] : null;
  const alloc = q ? q.minutes * 60 : READING;
  const phaseRemaining = alloc - (elapsed - phaseStart);
  const remaining = TOTAL - elapsed;
  const isLast = phase === c.questions.length - 1;
  const overTime = phaseRemaining <= 0;

  const moveOn = () => {
    rec.stop();
    setPhase((p) => p + 1);
    setPhaseStart(elapsed);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const finish = async () => {
    setSubmitting(true);
    await rec.flush();
    await new Promise((r) => setTimeout(r, 50));
    const latest = answersRef.current;
    const payload = {
      case_id: c.id,
      answers: c.questions.map((_, i) => ({ question_index: i, transcript: latest[i] || "" })),
      hints_used: Object.keys(hints).filter((k) => hints[k] > 0).map(Number),
      elapsed_seconds: elapsed,
    };
    if (isMock) {
      http.post("/feedback", payload)
        .then(({ data }) => { recordStation(c.id, data.job_id); nav("/mock"); })
        .catch((e) => { toast.error(errMsg(e)); setSubmitting(false); });
      return;
    }
    runJob("/feedback", payload)
      .then((r) => nav(`/results/${r.id}`))
      .catch((e) => { toast.error(errMsg(e)); setSubmitting(false); });
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 pb-24">
      <VivaTimer remaining={remaining} phaseLabel={q ? `Q${phase + 1} · ${q.minutes} min` : "Reading · 3 min"} phaseRemaining={phaseRemaining} />
      <Stem c={c} />
      {phase < 0 ? <ReadingNotes notes={notes} setNotes={setNotes} /> : <FloatingNotes notes={notes} setNotes={setNotes} />}

      <div className="mt-6 space-y-3">
        {c.questions.slice(0, Math.max(phase, 0)).map((pq, i) => <PastQuestion key={i} q={pq} i={i} transcript={answers[i]} pending={rec.pending[i] > 0} />)}
      </div>

      {q && (
        <section key={phase} data-testid="viva-question-card" className="card p-5 sm:p-7 mt-4 fade-up" style={{ borderColor: "var(--accent)" }}>
          <div className="flex items-center justify-between gap-3">
            <span className="font-mono text-xs font-bold" style={{ color: "var(--accent)" }}>QUESTION {phase + 1} OF {c.questions.length}</span>
            <span data-testid="viva-suggested-time-badge" className="font-mono text-xs px-2 py-1 rounded-md" style={{ background: "var(--muted-bg)" }}>{q.minutes} min suggested</span>
          </div>
          <QuestionPrompt prompt={q.prompt} key={`qp-${phase}`} />
          <ExaminerPrompts items={q.examiner_prompts} key={`ep-${phase}`} />
          <HintBox points={q.points} level={hints[phase] || 0} />
          <AnswerPanel
            index={phase}
            recording={rec.recordingIndex === phase}
            pending={rec.pending[phase] > 0}
            transcript={answers[phase] || ""}
            onChange={(v) => setAnswers((a) => ({ ...a, [phase]: v }))}
            onToggleRecord={() => (rec.recordingIndex === phase ? rec.stop() : rec.start(phase))}
            disabled={submitting}
            level={rec.level}
            silent={rec.silent}
          />
        </section>
      )}

      {overTime && !submitting && (
        <div data-testid="viva-move-on-suggestion" className="mt-4 rounded-xl px-4 py-3 text-sm font-medium animate-pulse" style={{ background: "color-mix(in srgb, var(--alert) 12%, transparent)", color: "var(--alert)" }}>
          {remaining <= 0 ? "Time's up for this station — finish to get feedback." : q ? `You've used the ${q.minutes} minutes allocated to this question — move on.` : "Reading time is over — move on to the first question."}
        </div>
      )}

      <div className="mt-6 flex flex-wrap items-center gap-3">
        {q && (
          <button
            data-testid="viva-hint-button"
            onClick={() => setHints((h) => ({ ...h, [phase]: Math.min((h[phase] || 0) + 1, 2) }))}
            disabled={(hints[phase] || 0) >= Math.min(2, q.points.length)}
            className="btn-ghost h-11 px-5 rounded-full text-sm font-medium flex items-center gap-2 disabled:opacity-50"
          >
            <Lightbulb size={16} /> {hints[phase] ? "Another hint" : "Hint"}
          </button>
        )}
        <div className="flex-1" />
        {!isLast ? (
          <button data-testid="viva-move-on-button" onClick={moveOn} className="btn-primary h-11 px-6 rounded-full font-semibold flex items-center gap-2">
            {phase < 0 ? "Start viva" : "Move on"} <ArrowRight size={16} />
          </button>
        ) : (
          <button data-testid="viva-finish-button" onClick={finish} disabled={submitting} className="btn-primary h-11 px-6 rounded-full font-semibold flex items-center gap-2">
            {submitting ? <><Loader2 size={16} className="animate-spin" /> {isMock ? "Submitting…" : "Examiner is marking…"}</> : <><Flag size={16} /> {isMock ? "Submit station & continue" : "Finish & get feedback"}</>}
          </button>
        )}
      </div>
    </div>
  );
}
